import type { MarkerValue, TreeNode, YearValue } from "@api/generated/model";

type Key = { paz: number; sid: number };

/** A line key as the breeders write it: "49281 / 1". */
export const keyText = (k: Key) => `${k.paz} / ${k.sid}`;

/** A line key in a URL or an API parameter: "49281/1". */
export const keyParam = (k: Key) => `${k.paz}/${k.sid}`;

/** A line's title on every screen: its display name (the server's rule), else its key. */
export const lineTitle = (l: { name?: string | null; key: Key }) => l.name ?? keyText(l.key);

/** A marker's call: +, − or the value itself (an allele notation, "?"); ``tone`` g for present. */
export function markText(m: MarkerValue): { mark: string; tone: string } {
  return { mark: m.present === true ? "+" : m.present === false ? "−" : m.value || "?", tone: m.present ? "g" : "" };
}

/** The yearly values of one field, oldest first, and the scale's top (a round number above the highest bar). */
export function yearBars(values: YearValue[], field: string): { bars: YearValue[]; top: number } {
  const bars = values.filter((v) => v.field === field).sort((a, b) => a.ev - b.ev);
  const high = Math.max(0, ...bars.flatMap((b) => [b.value ?? 0, b.trial_mean ?? 0]));
  const step = high > 0 ? 10 ** Math.floor(Math.log10(high)) / 2 : 1;
  return { bars, top: high > 0 ? Math.ceil(high / step) * step : 1 };
}

export type Placed = { node: TreeNode; x: number; y: number };

/** The tree laid out left to right: a generation per column, each end of the tree on its own row (mother above
 *  father), a node halfway between its parents. */
export function layoutTree(nodes: TreeNode[], col: number, row: number): { placed: Placed[]; height: number; width: number } {
  const parents = parentsByChild(nodes);
  const ys = new Map<number, number>();
  let next = 0;
  const place = (n: TreeNode): number => {
    const own = parents.get(n.id) ?? [];
    const y = own.length ? own.map(place).reduce((a, b) => a + b, 0) / own.length : next++ * row;
    ys.set(n.id, y);
    return y;
  };
  const root = nodes.find((n) => n.parent == null);
  if (root) place(root);
  const placed = nodes.map((node) => ({ node, x: node.generation * col, y: ys.get(node.id) ?? 0 }));
  const depth = Math.max(0, ...nodes.map((n) => n.generation));
  return { placed, height: Math.max(next, 1) * row, width: (depth + 1) * col };
}

/** For each gene, the ways it came into the root: from each carrier none of whose own ancestors in the tree carries
 *  it, down to the root (labels). ``carried``: node id → the genes the node's line carries. */
export function geneFlows(nodes: TreeNode[], carried: Map<number, string[]>, genes: string[]) {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const parentsOf = parentsByChild(nodes);
  const down = (n: TreeNode): TreeNode[] => {
    const out = [n];
    for (let p = n.parent; p != null; p = byId.get(p)?.parent ?? null) {
      const below = byId.get(p);
      if (below) out.push(below);
    }
    return out;
  };
  return genes.map((gene) => {
    const carries = (n: TreeNode) => carried.get(n.id)?.includes(gene) ?? false;
    // whether an ancestor of the node carries it: once per node (a carrier with one is covered by that one's path)
    const above = new Map<number, boolean>();
    const fromAbove = (n: TreeNode): boolean => {
      let known = above.get(n.id);
      if (known === undefined) {
        known = (parentsOf.get(n.id) ?? []).some((p) => carries(p) || fromAbove(p));
        above.set(n.id, known);
      }
      return known;
    };
    const oldest = nodes.filter((n) => n.generation > 0 && carries(n) && !fromAbove(n));
    return { gene, paths: oldest.map((n) => down(n).map((x) => x.label ?? x.text ?? "?")) };
  });
}

/** Each node's parents in the tree (mother first), by the node's id. */
export function parentsByChild(nodes: TreeNode[]): Map<number, TreeNode[]> {
  const parents = new Map<number, TreeNode[]>();
  for (const n of nodes) {
    if (n.parent == null) continue;
    const list = parents.get(n.parent) ?? [];
    list.push(n);
    parents.set(n.parent, list);
  }
  for (const list of parents.values()) list.sort((a, b) => (a.role === "mother" ? -1 : b.role === "mother" ? 1 : 0));
  return parents;
}

/** The ancestor search's depth in generations: the default and the most the server searches (many lines at once). */
export const TREE_DEPTH = 3;
export const TREE_MAX_DEPTH = 6;
/** The Pedigree tree page's depth (one line): opened 3 generations deep, any depth up to the server's 50 (a repeated
 *  ancestor is grown once, so a deep tree costs as much as its distinct ancestors). */
export const PEDIGREE_DEPTH = 3;
export const PEDIGREE_MAX_DEPTH = 50;
